import { Injectable } from '@angular/core';
import { OfflineStorageService } from './offline-storage.service';
import { PassportMainWithDraftDto } from '../interfaces/api-responses';

/**
 * Service for common passport caching operations
 * Eliminates duplication across persona components
 */
@Injectable({
  providedIn: 'root'
})
export class PassportCacheService {
  private cacheInProgress = false;

  constructor(private readonly offlineStorage: OfflineStorageService) {}

  /**
   * Check if caching is currently in progress
   */
  isCaching(): boolean {
    return this.cacheInProgress;
  }

  /**
   * Cache passports to offline storage
   * @param passports Array of passports to cache
   * @returns Promise that resolves when caching is complete
   */
  async cachePassports(passports: PassportMainWithDraftDto[]): Promise<void> {
    if (this.cacheInProgress) {
      console.log('Caching already in progress, skipping...');
      return;
    }

    this.cacheInProgress = true;

    try {
      // Cache each passport individually
      for (const p of passports) {
        if (p.passportID) {
          await this.offlineStorage.savePassport(p.passportID, p);
        }
      }
      console.log(`✅ Cached ${passports.length} passports to IndexedDB`);
    } catch (error) {
      console.warn('❌ Error caching passports:', error);
    } finally {
      this.cacheInProgress = false;
    }
  }

  /**
   * Placeholder for loading from offline storage
   * Can be enhanced in the future with bulk loading
   */
  async loadFromOfflineStorage(): Promise<PassportMainWithDraftDto[]> {
    try {
      console.log('📂 Loading passports from offline storage...');
      // For now, return empty array - bulk loading can be implemented later
      console.log('⚠️ Offline loading not fully implemented for bulk passports');
      return [];
    } catch (error) {
      console.warn('❌ Error loading from offline storage:', error);
      return [];
    }
  }
}

